import { useState } from 'react';

import { ConnectorErrorDetailsDrawer } from '../erps-crms/ConnectorErrorDetailsDrawer';
import { McpConnectorCard } from '../erps-crms/McpConnectorCard';
import { McpConnectorDeleteDialog } from '../erps-crms/McpConnectorDeleteDialog';
import { McpConnectorEditCredentialDrawer } from '../erps-crms/McpConnectorEditCredentialDrawer';
import { McpConnectorWizard } from '../erps-crms/McpConnectorWizard';
import { PauseIntegrationsDialog } from '../erps-crms/PauseIntegrationsDialog';
import { useIntegrationMetrics } from '../hooks/use-integration-reconciliation';
import { useIntegrationsFeatureStatus } from '../hooks/use-integrations-feature-status';
import { useMcpConnectors, useTestHandshake } from '../hooks/use-mcp-connectors';
import { MismatchDrawer } from '../reconciliation/MismatchDrawer';
import { ReconciliationPanel } from '../reconciliation/ReconciliationPanel';

export interface ErpsCrmsTabProps {
  mode: 'manage' | 'read-only';
}

/**
 * Sprint A4 S1 PR 3 — Task 6. Tab funcional ERPs/CRMs.
 *
 * Layout:
 *   1. Header com estado da feature (Ativa/Pausada + activeConnectorCount +
 *      lastUpdated). Modo manage mostra botão "Pausar feature" / "Reativar".
 *   2. Lista de cards filtrando `status='disabled'` (soft-deleted no backend).
 *   3. Empty state quando 0 conectores; CTA "+ Adicionar conector" em manage.
 *
 * Dialogs/drawers anchorados aqui via state — McpConnectorWizard (Task 7-9),
 * EditCredentialDrawer (Task 12), DeleteDialog (Task 13), PauseIntegrations
 * Dialog (Task 14), ErrorDetailsDrawer (Task 15). Stubs renderizáveis na
 * Task 6 pra ErpsCrmsTab compilar e exercitar abertura/fechamento.
 *
 * Modo `read-only` esconde botões de mutação; backend faz defense-in-depth.
 */
export function ErpsCrmsTab({ mode }: ErpsCrmsTabProps): JSX.Element {
  const { data: connectors, isLoading, error } = useMcpConnectors();
  const featureStatus = useIntegrationsFeatureStatus();
  const testHandshake = useTestHandshake();
  const { data: metrics } = useIntegrationMetrics();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editCredentialFor, setEditCredentialFor] = useState<string | null>(null);
  const [deleteFor, setDeleteFor] = useState<string | null>(null);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [errorDetailsFor, setErrorDetailsFor] = useState<string | null>(null);
  const [mismatchDrawerOpen, setMismatchDrawerOpen] = useState(false);

  const connectorNames: Record<string, string> = Object.fromEntries(
    (metrics?.connectors ?? []).map((c) => [c.externalSystemId, c.displayName]),
  );

  if (isLoading) {
    return <div className="p-8 text-sm text-text-secondary">Carregando conectores…</div>;
  }
  if (error !== null) {
    return <div className="p-8 text-sm text-red-600">Falha ao carregar: {error.message}</div>;
  }
  if (connectors === undefined) return <></>;

  const visibleConnectors = connectors.filter((c) => c.status !== 'disabled');

  return (
    <div className="flex flex-col gap-6 p-8">
      <ReconciliationPanel
        canManage={mode === 'manage'}
        onOpenMismatches={() => setMismatchDrawerOpen(true)}
      />

      <div className="flex items-center justify-between rounded-lg border border-border-subtle bg-surface px-4 py-3">
        <div>
          <p className="text-sm font-medium text-text-primary">
            Feature:{' '}
            {featureStatus.isPaused ? (
              <span className="text-gray-500">⏸ Pausada</span>
            ) : (
              <span className="text-green-600">✓ Ativa</span>
            )}
            {' · '}
            {featureStatus.activeConnectorCount} conector(es) ativo(s)
          </p>
          {featureStatus.lastUpdated !== null && (
            <p className="mt-0.5 text-xs text-text-secondary">
              Última atualização: {new Date(featureStatus.lastUpdated).toLocaleString('pt-BR')}
            </p>
          )}
        </div>
        {mode === 'manage' && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPauseDialogOpen(true)}
              className="rounded border border-border-subtle px-3 py-1.5 text-xs hover:bg-bg-muted"
            >
              {featureStatus.isPaused ? 'Reativar feature' : 'Pausar feature'}
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-text-primary">Conectores cadastrados</h2>
          {mode === 'manage' && (
            <button
              type="button"
              onClick={() => setWizardOpen(true)}
              className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover"
            >
              + Adicionar conector
            </button>
          )}
        </div>

        {visibleConnectors.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border-subtle bg-surface p-8 text-center text-sm text-text-secondary">
            Nenhum conector cadastrado ainda.{' '}
            {mode === 'manage' && 'Clique em "+ Adicionar conector" para começar.'}
          </div>
        ) : (
          visibleConnectors.map((c) => (
            <McpConnectorCard
              key={c.id}
              connector={c}
              mode={mode}
              onRefresh={() => testHandshake.mutate(c.id)}
              onEditCredential={() => setEditCredentialFor(c.id)}
              onDelete={() => setDeleteFor(c.id)}
              onSeeErrorDetails={() => setErrorDetailsFor(c.id)}
            />
          ))
        )}
      </div>

      <McpConnectorWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
      {editCredentialFor !== null && (
        <McpConnectorEditCredentialDrawer
          connectorId={editCredentialFor}
          onClose={() => setEditCredentialFor(null)}
        />
      )}
      {deleteFor !== null && (
        <McpConnectorDeleteDialog connectorId={deleteFor} onClose={() => setDeleteFor(null)} />
      )}
      {pauseDialogOpen && (
        <PauseIntegrationsDialog
          mode={featureStatus.isPaused ? 'resume' : 'pause'}
          affectedCount={featureStatus.activeConnectorCount}
          onClose={() => setPauseDialogOpen(false)}
        />
      )}
      {errorDetailsFor !== null && (
        <ConnectorErrorDetailsDrawer
          connectorId={errorDetailsFor}
          onClose={() => setErrorDetailsFor(null)}
        />
      )}

      <MismatchDrawer
        isOpen={mismatchDrawerOpen}
        onClose={() => setMismatchDrawerOpen(false)}
        canManage={mode === 'manage'}
        connectorNames={connectorNames}
      />
    </div>
  );
}
